import React, { useEffect, useRef, useState } from 'react';
import { MdClose, MdDownload, MdFullscreen } from 'react-icons/md';
import { cn } from '../../../lib/utils';
import type { AdminOfferLike } from '../../../utils/offer-pdf';
import { buildAdminOfferPdfBlob, downloadAdminOfferPDF } from '../../../utils/offer-pdf';
import type { RfqPageUi } from '../create-offer-theme';

interface OfferPdfPreviewSectionProps {
  offer: AdminOfferLike;
  ui: RfqPageUi;
  isDark: boolean;
}

export function OfferPdfPreviewSection({ offer, ui, isDark }: OfferPdfPreviewSectionProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const urlRef = useRef<string | null>(null);
  const offerKey = JSON.stringify(offer);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsGenerating(true);
      try {
        const blob = await buildAdminOfferPdfBlob(offer);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setPdfUrl(url);
      } catch {
        if (!cancelled) setPdfUrl(null);
      } finally {
        if (!cancelled) setIsGenerating(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [offerKey, offer]);

  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, []);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadAdminOfferPDF(offer);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div id="offer-pdf-preview" className={ui.section}>
        <div className={cn(ui.sectionHeader, 'flex-wrap gap-4')}>
          <div className="flex min-w-0 items-center gap-3">
            <img
              src="/euroasiann-offer-logo.png"
              alt="Euroasiann Group"
              className="h-10 w-10 rounded-md object-contain bg-black"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/euroasiann-logo-light.png';
              }}
            />
            <div className="min-w-0">
              <h2 className={ui.sectionTitle}>Offer Preview</h2>
              <p className={cn('text-[11px] font-semibold uppercase tracking-wider', ui.label)}>
                Euroasiann Group
              </p>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              disabled={!pdfUrl || isGenerating}
              className={cn(
                ui.cmdBtn,
                'min-h-[40px] min-w-[140px] px-4 disabled:cursor-not-allowed disabled:opacity-50',
                isDark
                  ? 'border border-white/[0.12] bg-[#111827] text-slate-200 hover:border-white/20 hover:bg-[#1e293b]'
                  : 'border border-[#cbd5e1] bg-white text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5'
              )}
            >
              <MdFullscreen className="h-4 w-4" />
              Enlarge
            </button>
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading || isGenerating}
              className={cn(
                ui.cmdBtn,
                'min-h-[40px] min-w-[180px] px-4 disabled:cursor-not-allowed disabled:opacity-50',
                ui.sendBtn
              )}
            >
              <MdDownload className="h-4 w-4" />
              {isDownloading ? 'Downloading...' : 'Download Offer'}
            </button>
          </div>
        </div>

        <div
          className={cn(
            'relative overflow-hidden rounded-xl border',
            isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-[#f8fafc]'
          )}
        >
          {isGenerating && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20 backdrop-blur-[1px]">
              <p className={cn('text-sm font-semibold uppercase tracking-wider', ui.label)}>
                Updating preview...
              </p>
            </div>
          )}
          {pdfUrl ? (
            <iframe
              title="Offer PDF preview"
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              className="h-[420px] w-full border-0 bg-white"
            />
          ) : (
            <div className="flex h-[420px] items-center justify-center px-6 text-center">
              <p className={cn('text-sm font-medium', ui.subtitle)}>
                {isGenerating ? 'Generating offer PDF...' : 'Fill in offer details to preview the PDF.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showModal && pdfUrl && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div
            className={cn(
              'flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl shadow-2xl',
              isDark ? 'border border-white/[0.08] bg-[#111827]' : 'border border-[#0088cc]/20 bg-white'
            )}
          >
            <div
              className={cn(
                'flex items-center justify-between border-b px-5 py-4',
                isDark ? 'border-white/[0.08]' : 'border-[#0088cc]/15'
              )}
            >
              <div>
                <h3 className={cn('text-lg font-bold', isDark ? 'text-white' : 'text-[hsl(var(--foreground))]')}>
                  Offer PDF Preview
                </h3>
                <p className={cn('text-xs font-medium uppercase tracking-wider', ui.label)}>
                  {offer.quotationNumber || 'PREVIEW'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleDownload()}
                  disabled={isDownloading}
                  className={cn(ui.cmdBtn, 'min-h-[40px] px-4 disabled:opacity-50', ui.sendBtn)}
                >
                  <MdDownload className="h-4 w-4" />
                  {isDownloading ? 'Downloading...' : 'Download Offer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-lg border transition-colors',
                    isDark
                      ? 'border-white/[0.12] text-slate-300 hover:bg-white/[0.06]'
                      : 'border-[#cbd5e1] text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/5'
                  )}
                  aria-label="Close preview"
                >
                  <MdClose className="h-5 w-5" />
                </button>
              </div>
            </div>
            <iframe
              title="Offer PDF full preview"
              src={pdfUrl}
              className="min-h-0 flex-1 w-full border-0 bg-white"
            />
          </div>
        </div>
      )}
    </>
  );
}
